/**
 * StatusBadge — Enterprise Design System
 *
 * Color-coded status indicator badge with optional dot for severity statuses.
 *
 * Props:
 *   - status (string): Status value to display (case-insensitive matching)
 *   - size ('small'|'medium'): Badge height — small: 22px (default), medium: 28px
 *   - label (string, optional): Override text to display instead of status value
 *
 * Supported statuses: Active, Inactive, Pending, Approved, Rejected,
 *   Draft, Archived, Critical, Healthy, Warning
 * Unknown statuses render with neutral fallback styling.
 *
 * Requirements: 10.1, 10.2, 10.3, 10.4, 10.5, 10.6, 10.7
 */
import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';

// ── Status Color Map (WCAG AA 4.5:1 compliant) ───────────────────────────────
export const STATUS_COLOR_MAP = {
  // ── Success states ──────────────────────────────────────────────────────────
  active: { bg: '#ecfdf5', text: '#065f46', darkBg: 'rgba(5,150,105,0.15)', darkText: '#6ee7b7' },
  success: { bg: '#ecfdf5', text: '#065f46', darkBg: 'rgba(5,150,105,0.15)', darkText: '#6ee7b7' },
  healthy: { bg: '#ecfdf5', text: '#047857', dot: true, darkBg: 'rgba(4,120,87,0.15)', darkText: '#6ee7b7' },
  resolved: { bg: '#ecfdf5', text: '#065f46', darkBg: 'rgba(5,150,105,0.15)', darkText: '#6ee7b7' },
  completed: { bg: '#ecfdf5', text: '#065f46', darkBg: 'rgba(5,150,105,0.15)', darkText: '#6ee7b7' },
  approved: { bg: '#eff6ff', text: '#1e40af', darkBg: 'rgba(37,99,235,0.15)', darkText: '#93c5fd' },

  // ── Warning states ──────────────────────────────────────────────────────────
  pending: { bg: '#fffbeb', text: '#92400e', darkBg: 'rgba(217,119,6,0.15)', darkText: '#fcd34d' },
  warning: { bg: '#fffbeb', text: '#854d0e', dot: true, darkBg: 'rgba(217,119,6,0.15)', darkText: '#fcd34d' },
  'in progress': { bg: '#eff6ff', text: '#1e40af', dot: true, darkBg: 'rgba(37,99,235,0.15)', darkText: '#93c5fd' },
  'in_progress': { bg: '#eff6ff', text: '#1e40af', dot: true, darkBg: 'rgba(37,99,235,0.15)', darkText: '#93c5fd' },
  inprogress: { bg: '#eff6ff', text: '#1e40af', dot: true, darkBg: 'rgba(37,99,235,0.15)', darkText: '#93c5fd' },
  open: { bg: '#eff6ff', text: '#1e40af', darkBg: 'rgba(37,99,235,0.15)', darkText: '#93c5fd' },

  // ── Danger states ───────────────────────────────────────────────────────────
  critical: { bg: '#fef2f2', text: '#b91c1c', dot: true, darkBg: 'rgba(220,38,38,0.15)', darkText: '#fca5a5' },
  rejected: { bg: '#fef2f2', text: '#991b1b', darkBg: 'rgba(220,38,38,0.15)', darkText: '#fca5a5' },
  failed: { bg: '#fef2f2', text: '#991b1b', darkBg: 'rgba(220,38,38,0.15)', darkText: '#fca5a5' },
  overdue: { bg: '#fef2f2', text: '#991b1b', dot: true, darkBg: 'rgba(220,38,38,0.15)', darkText: '#fca5a5' },

  // ── Neutral states ──────────────────────────────────────────────────────────
  inactive: { bg: '#f1f5f9', text: '#475569', darkBg: 'rgba(100,116,139,0.15)', darkText: '#94a3b8' },
  draft: { bg: '#f8fafc', text: '#334155', darkBg: 'rgba(51,65,85,0.15)', darkText: '#cbd5e1' },
  closed: { bg: '#f1f5f9', text: '#475569', darkBg: 'rgba(100,116,139,0.15)', darkText: '#94a3b8' },
  cancelled: { bg: '#f1f5f9', text: '#64748b', darkBg: 'rgba(100,116,139,0.15)', darkText: '#94a3b8' },
  canceled: { bg: '#f1f5f9', text: '#64748b', darkBg: 'rgba(100,116,139,0.15)', darkText: '#94a3b8' },
  archived: { bg: '#f5f3ff', text: '#5b21b6', darkBg: 'rgba(124,58,237,0.15)', darkText: '#c4b5fd' },
  expired: { bg: '#f1f5f9', text: '#64748b', darkBg: 'rgba(100,116,139,0.15)', darkText: '#94a3b8' },

  // ── Info states ─────────────────────────────────────────────────────────────
  scheduled: { bg: '#eff6ff', text: '#1e40af', darkBg: 'rgba(37,99,235,0.15)', darkText: '#93c5fd' },
  assigned: { bg: '#eff6ff', text: '#1e40af', darkBg: 'rgba(37,99,235,0.15)', darkText: '#93c5fd' },
  'on hold': { bg: '#fef3c7', text: '#92400e', darkBg: 'rgba(217,119,6,0.15)', darkText: '#fcd34d' },
  'on_hold': { bg: '#fef3c7', text: '#92400e', darkBg: 'rgba(217,119,6,0.15)', darkText: '#fcd34d' },
};

// Neutral fallback for unknown statuses
const FALLBACK_COLORS = { bg: '#f1f5f9', text: '#64748b', darkBg: 'rgba(100,116,139,0.15)', darkText: '#94a3b8' };

// ── Size Definitions (consistent across all badges) ───────────────────────────
const SIZES = {
  small: { height: 22, fontSize: '0.6875rem', px: 8, dotSize: 6, minWidth: 80 },
  medium: { height: 28, fontSize: '0.75rem', px: 10, dotSize: 7, minWidth: 96 },
};

/**
 * StatusBadge Component
 */
function StatusBadge({ status, size = 'small', label }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const normalizedStatus = (status || '').toLowerCase().trim();
  const colorConfig = STATUS_COLOR_MAP[normalizedStatus] || FALLBACK_COLORS;
  const sizeConfig = SIZES[size] || SIZES.small;
  const displayLabel = label || status || '';
  const showDot = colorConfig.dot === true;

  const bgColor = isDark ? colorConfig.darkBg : colorConfig.bg;
  const textColor = isDark ? colorConfig.darkText : colorConfig.text;

  return (
    <Box
      component="span"
      role="status"
      aria-label={`Status: ${displayLabel}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: showDot ? '5px' : 0,
        height: `${sizeConfig.height}px`,
        px: `${sizeConfig.px}px`,
        minWidth: `${sizeConfig.minWidth}px`,
        borderRadius: '9999px',
        backgroundColor: bgColor,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        maxWidth: '100%',
      }}
    >
      {showDot && (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            width: `${sizeConfig.dotSize}px`,
            height: `${sizeConfig.dotSize}px`,
            borderRadius: '50%',
            backgroundColor: textColor,
            flexShrink: 0,
          }}
        />
      )}
      <Typography
        component="span"
        sx={{
          fontSize: sizeConfig.fontSize,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
          lineHeight: 1,
          color: textColor,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {displayLabel}
      </Typography>
    </Box>
  );
}

export default StatusBadge;
